import { IconLoader2 } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { ResolutionOption, ResolutionOptionId } from "../lib/overrideEditModel"

/**
 * The resolution choice (AB#806): asked before saving an override that leaves booked appointments in
 * its window. Lists the options this build has, as data (overrideEditModel's RESOLUTION_OPTIONS) —
 * "Proceed without rescheduling", "Automatic reassignment" (M14, AB#810) and "Manual reschedule" —
 * the last two as the server says (resolve/capabilities before Save, resolve/options after; manual is
 * disabled until M15). An option that is listed but cannot be taken is drawn disabled, with why.
 * The override's detail asks the same about an override already saved, with its own `prompt` and
 * `cancelLabel`.
 */
export function ResolutionChoiceDialog({
  open,
  count,
  options,
  busy,
  onChoose,
  onCancel,
  prompt = "Choose what to do about them before the override is saved.",
  cancelLabel = "Back to the override",
}: {
  open: boolean
  /** How many booked appointments the preview listed. */
  count: number
  options: readonly ResolutionOption[]
  busy: boolean
  onChoose: (id: ResolutionOptionId) => void
  onCancel: () => void
  /** The sentence after the count. */
  prompt?: string
  cancelLabel?: string
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onCancel()}>
      <DialogContent className="sm:max-w-lg" data-testid="resolution-choice">
        <DialogHeader>
          <DialogTitle>What about the booked appointments?</DialogTitle>
          <DialogDescription>
            {count === 1
              ? "1 booked appointment falls in this override's window."
              : `${count} booked appointments fall in this override's window.`}
            {" "}{prompt}
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-2" aria-label="Resolution options">
          {options.map(option => (
            <li key={option.id} className="flex flex-col gap-1 rounded-lg border p-3" data-resolution-option={option.id}>
              <Button
                className="self-start"
                size="sm"
                onClick={() => onChoose(option.id)}
                disabled={busy || !option.enabled}
              >
                {busy && option.enabled && <IconLoader2 className="size-4 animate-spin" />}
                {option.label}
              </Button>
              <p className="text-xs text-muted-foreground">{option.description}</p>
              {!option.enabled && option.reason && <p className="text-xs text-muted-foreground">{option.reason}</p>}
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
